// import { colors } from "../design-system.framerfx/code/canvas"
import * as React from 'react';
import { theme as baseTheme, useTheme } from '@chakra-ui/core';
import { Global, css } from '@emotion/core';
import { colors } from './base/colors';
import icons from './base/icons';
import { fonts, fontSizes, fontWeights, lineHeights, letterSpacings } from './base/typography';
import CalibreRegular from './fonts/CalibreRegular.woff';
import CalibreMedium from './fonts/CalibreMedium.woff';
import CalibreSemibold from './fonts/CalibreSemibold.woff';
import CalibreBold from './fonts/CalibreBold.woff';
import InterRegular from './fonts/Inter-Regular.woff';
import InterMedium from './fonts/Inter-Medium.woff';
import InterSemibold from './fonts/Inter-SemiBold.woff';
import InterBold from './fonts/Inter-Bold.woff';

const deepmerge = require('deepmerge');

const GlobalStyle: React.FC<any> = (props: any): React.ReactElement => {
    let { theme } = props || {};
    if (!theme) {
        theme = useTheme();
    }
    return (
        <Global
            styles={css`
                @font-face {
                    font-family: "Inter";
                    src: url(${InterRegular}) format("truetype");
                    font-weight: 400;
                    font-style: normal;
                }
                @font-face {
                    font-family: "Inter";
                    src: url(${InterSemibold}) format("truetype");
                    font-weight: 600;
                    font-style: normal;
                }
                @font-face {
                    font-family: "Inter";
                    src: url(${InterBold}) format("truetype");
                    font-weight: 700;
                    font-style: normal;
                }
                @font-face {
                    font-family: "Calibre";
                    src: url(${CalibreMedium}) format("truetype");
                    font-weight: 500;
                    font-style: normal;
                }
                @font-face {
                    font-family: "Calibre";
                    src: url(${CalibreSemibold}) format("truetype");
                    font-weight: 600;
                    font-style: normal;
                }
                @font-face {
                    font-family: "Calibre";
                    src: url(${CalibreBold}) format("truetype");
                    font-weight: 700;
                    font-style: normal;
                }
                html,
                body {
                    background-color: ${theme.colors.bg.base.primary};
                    color: ${theme.colors.text.base.primary};
                    font-family: ${theme.fonts.body};
                    font-size: 16px;
                    line-height: normal;
                }
            `}
        />
    );
};

export const createMediaQuery = (n: string, type: 'min' | 'max' = 'min'): string => {
    if (type === 'max') {
        return `@media screen and (max-width: ${n})`;
    }
    return `@media screen and (min-width: ${n})`;
};

export const createMediaRangeQuery = (min: string, max: string): string => `@media screen and (min-width: ${min}) and (max-width: ${adjustBreakpoint(max, -0.2)})`;

const addAliases = (arr: string[], aliases: string[]) => {
    aliases.forEach((key, i) => {
        Object.defineProperty(arr, key, {
            enumerable: false,
            get() {
                return this[i];
            }
        });
    });
};

const adjustBreakpoint = (breakpoint: string, shift: number): string => {
    const value = parseInt(breakpoint.replace(/[^\d\.]/, ''), 10) + shift;
    return `${value}px`;
};

// export const breakpoints = [32, 40, 48, 64].map(n => `${n}em`);
export const breakpoints = [512, 640, 768, 1024, 1200, 1376].map((n) => `${n}px`);
export const namedBreakpoints = {
    tablet: breakpoints[1],
    desktop: breakpoints[3]
};
export const mediaQueries = breakpoints.map((item) => createMediaQuery(item, 'min'));
export const mediaRangeQueries = {
    xs: createMediaQuery(breakpoints[0], 'max'),
    sm: createMediaRangeQuery(breakpoints[0], breakpoints[1]),
    md: createMediaRangeQuery(breakpoints[1], breakpoints[2]),
    lg: createMediaRangeQuery(breakpoints[2], breakpoints[3]),
    xl: createMediaRangeQuery(breakpoints[3], breakpoints[4]),
    xxl: createMediaQuery(breakpoints[4], 'min'),
    print: '@media print',
    mobile: createMediaQuery(namedBreakpoints.tablet, 'max'),
    tablet: createMediaRangeQuery(namedBreakpoints.tablet, namedBreakpoints.desktop),
    desktop: createMediaQuery(namedBreakpoints.desktop, 'min'),
    'sm-up': createMediaQuery(breakpoints[0], 'min'),
    'md-up': createMediaQuery(breakpoints[1], 'min'),
    'lg-up': createMediaQuery(breakpoints[2], 'min'),
    'xl-up': createMediaQuery(breakpoints[3], 'min'),
    'tablet-up': createMediaQuery(namedBreakpoints.tablet, 'min'),
    'desktop-up': createMediaQuery(namedBreakpoints.desktop, 'min'),
    'xs-down': createMediaQuery(breakpoints[0], 'max'),
    'sm-down': createMediaQuery(breakpoints[1], 'max'),
    'md-down': createMediaQuery(breakpoints[2], 'max'),
    'lg-down': createMediaQuery(breakpoints[3], 'max'),
    'xl-down': createMediaQuery(breakpoints[4], 'max'),
    'mobile-down': createMediaQuery(adjustBreakpoint(namedBreakpoints.tablet, -0.2), 'max'),
    'tablet-down': createMediaQuery(adjustBreakpoint(namedBreakpoints.desktop, -0.2), 'max')
};
export const sizeAliases = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];

export const space = [0, 4, 8, 16, 32, 64, 128];
export const border = [0, 1, 2, 4, 8, 12, 18];

// const colors = {

// }

// styled-system's `borderRadius` function can hook into the `radii` object/array
export const radii = [0, 3, 5, 8, 12];
export const radius = '2px';

export const shadows = {
    none: 0,
    xs: '0 0 2px 0 rgba(0,0,0,.04), 0 1px 4px 0 rgba(0,0,0,.08)',
    sm: '0 0 2px 0 rgba(0,0,0,.04), 0 1px 4px 0 rgba(0,0,0,.16)',
    md: '0 0 2px 0 rgba(0,0,0,.04), 0 2px 8px 0 rgba(0,0,0,.16)',
    lg: '0 0 2px 0 rgba(0,0,0,.04), 0 4px 16px 0 rgba(0,0,0,.16)',
    xl: '0 0 2px 0 rgba(0,0,0,.04), 0 8px 32px 0 rgba(0,0,0,.16)'
};

export const components = {
    grid: {
        margin: `${space[3]}px`
    },
    // link: {
    //     default: {
    //         color: colors.grays[8]
    //     },
    //     hover: {
    //         color: colors.grays[9]
    //     },
    //     visited: {
    //         color: colors.grays[9]
    //     }
    // },
    button: {
        base: {
            hover: {
                color: 'grays.2'
            },
            disabled: {
                color: 'grays.4'
            }
        },
        dark: {
            bg: 'grays.9',
            color: 'text.light',
            hover: {
                bg: 'grays.7'
            },
            disabled: {
                bg: 'grays.5'
            }
        },
        brand: {
            bg: 'brand.green.base',
            color: 'text.light',
            hover: {
                bg: 'brand.green.levels.2'
            },
            disabled: {
                bg: 'brand.green.levels.5'
            }
        },
        outline: {
            borderColor: 'text.dark',
            hover: {
                borderColor: 'grays.6'
            },
            disabled: {
                borderColor: 'grays.4',
                color: 'grays.4'
            }
        }
    },
    card: {
        bg: 'white',
        p: 3,
        radius: 3
    },
    textInput: {
        bg: 'transparent'
    },
    textarea: {},
    // checkbox: {
    //     color: colors.grays[8],
    //     fill: colors.grays[8],
    //     fontSize: 2,
    //     disabled: {
    //         color: colors.grays[5],
    //         fill: colors.grays[5]
    //     }
    // },
    select: {},
    formField: {
        container: {
            my: 3
        },
        label: {
            fontSize: 'sm',
            fontWeight: 'bold'
        },
        help: {
            color: 'brand.gray.700',
            fontSize: 'xs',
            my: '4px'
        },
        error: {
            color: 'text.error',
            fontSize: 'xs',
            my: '4px'
        }
    },
    table: {
        header: {
            px: 2,
            py: 1,
            fontFamily: 'label',
            fontSize: 'xs',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: 'widest',
            borderBottomStyle: 'solid',
            borderBottomWidth: '3px',
            borderBottomColor: 'brand.green.base'
        },
        body: {
            p: 2,
            borderBottomStyle: 'solid',
            borderBottomWidth: '1px',
            borderBottomColor: 'brand.white.200'
        },
        footer: {}
    }
};

// const theme = {
//   breakpoints,
//   mediaQueries,
//   // space,
//   font,
//   fontSizes,
//   // fontWeights,
//   // lineHeights,
//   // letterSpacings,
//   // regular,
//   // bold,
//   // textStyles: {
//   //   fontSizes,
//   //   fontWeights,
//   //   lineHeights,
//   //   letterSpacings
//   // },
//   colors,
//   // colorStyles,
//   // radii,
//   // radius,
//   // boxShadows,
//   // maxContainerWidth,
//   // duration,
//   // timingFunctions,
//   // transitionDelays
// }

const theme = deepmerge(baseTheme, {
    breakpoints,
    mediaQueries,
    space,
    sizes: {
        full: '100%',
        '3xs': '14rem',
        '2xs': '16rem',
        xs: '20rem',
        sm: '24rem',
        md: '28rem',
        lg: '32rem',
        xl: '36rem',
        '2xl': '42rem',
        '3xl': '48rem',
        '4xl': '56rem',
        '5xl': '64rem',
        '6xl': '72rem'
    },
    fonts,
    fontSizes,
    fontWeights,
    lineHeights,
    letterSpacings,
    colors,
    icons,
    radii: {
        xl: '0.75em'
    },
    shadows,
    components
});

export { GlobalStyle };

export default theme;
